import { useEffect, useState } from "react"
import { useOutletContext } from "react-router"
import { PencilSimple } from "@phosphor-icons/react"
import { t } from "i18next"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { Button } from "@/components/ui/button"
import { coveragePrimaryPriority } from "@/lib/utils"
import { UpdatePatientWizard } from "@/modules/inbound-wizard"
import { isDeInboundCallAvailableV2, UpdateDePatientWizardV2 } from "@/modules/inbound-wizard/de-v2"
import { PatientData } from "@/modules/patients/detail/store/usePatientStore"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { App } from "@/modules/patients/information/app/app.tsx"
import { Cave, CaveEntryType } from "@/modules/patients/information/cave/cave.tsx"
import { Consent } from "@/modules/patients/information/consent/consent.tsx"
import { Contact } from "@/modules/patients/information/contact/contact.tsx"
import { Coverage, CoverageProps } from "@/modules/patients/information/coverage/coverage.tsx"
import { DoctorsNetwork } from "@/modules/patients/information/doctors-network/doctors-network.tsx"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { chPatientByIdQuery$data } from "@/modules/patients/services/ch/__generated__/chPatientByIdQuery.graphql"
import { Insuree } from "@/modules/patients/services/usePatientByIdQuery"
import { ContactChannel } from "@/types/__generated__/union-types"

type CoverageType = Insuree["coverages"][number]

interface PatientInformationProps {
  patient?: PatientFragment$data
  emailAddress?: string
  phoneNumber?: string
  isEditMode?: boolean
  caveEntry?: CaveEntryType
  socialSecurityNumber?: string | null
  insuree: Insuree | undefined
  insurees: NonNullable<chPatientByIdQuery$data["patientById"]>["insurees"]
  doctorNetworks?: NonNullable<chPatientByIdQuery$data["patientById"]>["doctorNetworks"]
  contactChannel?: ContactChannel
  emailVerification?: NonNullable<chPatientByIdQuery$data["patientById"]>["emailVerification"]
  account: NonNullable<PatientData>["account"]
  setIsUpdateDialogOpen?: (open: boolean) => void
}

const PatientInformation = () => {
  const {
    patient,
    emailAddress,
    phoneNumber,
    isEditMode,
    caveEntry,
    socialSecurityNumber,
    insurees,
    doctorNetworks,
    contactChannel,
    emailVerification,
    account,
    setIsUpdateDialogOpen,
  } = useOutletContext<PatientInformationProps>()
  const country = useCountryParam()

  const [open, setOpen] = useState(false)
  const [isDeUpdateWizardV2Open, setIsDeUpdateWizardV2Open] = useState(false)

  useEffect(() => {
    setIsUpdateDialogOpen?.(open || isDeUpdateWizardV2Open)
  }, [open, isDeUpdateWizardV2Open, setIsUpdateDialogOpen])

  if (!patient) {
    return
  }

  const mapCoverage = (coverage: CoverageType, insuree: Insuree) => {
    const { organisation, insureeNumber } = insuree

    const { isPrimary } = coverage.product
    const { shouldContainInsuranceCardNumber = null } = coverage.product.productConfiguration ?? {}
    return {
      id: coverage.id,
      insureeId: insuree?.id ?? "",
      organisationId: organisation?.id ?? "",
      organisationName: organisation?.name ?? "",
      insuranceCardNumber: "healthInsuranceCardNumber" in insuree ? (insuree.healthInsuranceCardNumber ?? "") : "",
      insureeNumber: insureeNumber ?? "",
      validFrom: coverage?.validFrom ?? "",
      validTo: coverage?.validTo ?? "",
      updatedAt: coverage?.updatedAt ?? "",
      isPrimary,
      productName: coverage.product.name,
      policyNumber: "policyNumber" in coverage ? (coverage.policyNumber ?? "") : "",
      productId: coverage.product.id,
      shouldContainInsuranceCardNumber,
    }
  }

  const coverages: CoverageProps[] = insurees.flatMap((insuree) =>
    insuree.coverages.map((coverage) => mapCoverage(coverage, insuree)),
  )

  const activeCoverages: CoverageProps[] = insurees.flatMap((insuree) =>
    insuree.activeCoverages.map((coverage) => mapCoverage(coverage, insuree)),
  )

  const sortedCoverages = coverages.sort((a, b) => {
    return coveragePrimaryPriority(a.isPrimary) - coveragePrimaryPriority(b.isPrimary)
  })
  const sortedActiveCoverages = activeCoverages.sort((a, b) => {
    return coveragePrimaryPriority(a.isPrimary) - coveragePrimaryPriority(b.isPrimary)
  })

  const patientName = `${patient.firstName} ${patient.lastName}`
  const shouldShowDeUpdateWizardV2Button = isEditMode && isDeInboundCallAvailableV2(country)

  // TODO This is used for testing new DE v2 only, will be replaced in future
  const renderEditButtons = (editButtonTestId: string, deUpdateWizardV2ButtonTestId: string) =>
    isEditMode && (
      <div className="flex items-center gap-2">
        {shouldShowDeUpdateWizardV2Button && (
          <Button
            data-testid={deUpdateWizardV2ButtonTestId}
            variant="tertiary"
            onClick={() => setIsDeUpdateWizardV2Open(true)}>
            {t("patient.information.editDeInboundCallV2")}
          </Button>
        )}
        <Button
          data-testid={editButtonTestId}
          variant="circle"
          className="flex h-10 w-10 cursor-pointer items-center justify-center p-0"
          onClick={() => setOpen(true)}>
          <PencilSimple className="text-on-surface-subdued" />
        </Button>
      </div>
    )

  return (
    <main className="flex min-w-0 flex-col">
      <div
        data-testid="patient-information"
        className="border-opacity-10 text-on-surface bg-surface inline-flex h-18 w-full items-center justify-between border-b p-8 py-4.5 text-xl font-bold">
        {t("patient.information.patientInformation")}
      </div>
      <ScrollWrapper>
        <div className="flex flex-col gap-4 p-8 lg:flex-row">
          <div className="flex min-w-0 flex-col gap-4 lg:w-1/2">
            <Contact
              {...patient}
              patientId={patient.id}
              isEditMode={isEditMode}
              socialSecurityNumber={socialSecurityNumber}
              emailAddress={emailAddress}
              phoneNumber={phoneNumber}
              contactChannel={contactChannel}
              emailVerification={emailVerification}
              appAccount={account}>
              {renderEditButtons("edit-information-button", "edit-information-de-button-v2")}
            </Contact>
            <Coverage coverages={sortedCoverages} activeCoverages={sortedActiveCoverages}>
              {renderEditButtons("edit-coverage-button", "edit-coverage-de-button-v2")}
            </Coverage>
          </div>
          <div className="flex flex-col gap-4 lg:w-1/4">
            <DoctorsNetwork doctors={doctorNetworks} isEditMode={isEditMode} />
            <Cave isEditMode={isEditMode} caveEntry={caveEntry} country={patient.country} />
          </div>
          <div className="flex flex-col gap-4 lg:w-1/4">
            <App appAccount={account} />
            <Consent
              patientId={patient.id}
              dataGuardPatientIdentifier={patient.patientId}
              country={patient.country}
              contactChannel={contactChannel}
              isEditMode={isEditMode}
              phoneNumber={patient.phoneNumber}
            />
          </div>
        </div>
      </ScrollWrapper>
      {open && (
        <UpdatePatientWizard
          defaultCountry={country}
          patientId={patient.id}
          patientName={patientName}
          open={open}
          onOpenChange={setOpen}
          shouldHideCallReason={true}
        />
      )}
      {isDeUpdateWizardV2Open && (
        <UpdateDePatientWizardV2
          patientId={patient.id}
          patientName={patientName}
          open={isDeUpdateWizardV2Open}
          onOpenChange={setIsDeUpdateWizardV2Open}
          shouldHideCallReason
        />
      )}
    </main>
  )
}
export { PatientInformation, type PatientInformationProps }
